<template>
    <CModal :model-value="modelValue" title="Upraviť URL adresu" @update:model-value="handleModalClose">
        <CFormGroup
            description="Slúži na optimalizáciu pre vyhľadávače (SEO). Preto nie je ideálne ho dodatočne meniť."
            :error="errors.slug"
            label="Trvalý odkaz"
        >
            <CFormInput v-model="data.slug" autocomplete="off" autofocus>
                <template #prefix>
                    <div class="-mr-4 pl-4 pt-px text-gray-500 dark:text-txt-body">
                        {{ baseUrl }}
                    </div>
                </template>
            </CFormInput>
        </CFormGroup>

        <template #footer>
            <div class="flex justify-end">
                <CButton @click="close()">Zrušiť</CButton>
                <CButton variant="primary" @click="submit">Použiť tento odkaz</CButton>
            </div>
        </template>
    </CModal>
</template>

<script setup lang="ts">
import { useSlug } from '@/composables/slug/useSlug'
import { isAvailableSlug } from '@/validators/isAvailableSlug'
import type { ValidationErrors } from 'last-validator'
import { hasMinLength, isRequired, validate } from 'last-validator'

const props = defineProps<{
    modelValue: boolean
    collection: string
    path?: string
    slug?: string
    excludeId?: string
}>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'close', value?: string): void
}>()

const { slugify } = useSlug()
const errors = ref<ValidationErrors>({})
const data = ref({ slug: props.slug || '' })

const baseUrl = computed((): string => {
    const val = props.path ? [window.location.origin, props.path] : [window.location.origin]
    return val.join('/') + '/'
})

onMounted(() => {
    if (props.slug) {
        data.value.slug = props.slug
    }
})

watch(
    () => props.slug,
    (newSlug) => {
        if (newSlug) {
            data.value.slug = newSlug
        }
    }
)

watch(
    () => props.modelValue,
    (isOpen) => {
        if (isOpen && props.slug) {
            data.value.slug = props.slug
            errors.value = {}
        }
    }
)

function handleModalClose(value: boolean) {
    if (!value) {
        close()
    }
}

function close(result?: string) {
    emit('update:modelValue', false)
    emit('close', result)
}

async function validateSlug() {
    const { isValid, errors: validationErrors } = await validate(data.value, {
        slug: [isRequired('Povinný údaj'), hasMinLength(3), isAvailableSlug(props.collection, props.excludeId)]
    })

    errors.value = validationErrors
    return isValid
}

async function submit() {
    data.value.slug = slugify(data.value.slug)
    const isValid = await validateSlug()

    if (!isValid) {
        return
    }

    close(data.value.slug)
}

defineExpose({
    getValue: () => data.value.slug
})
</script>
